import { useState } from 'react';
import {
	DEFAULT_CERTIFICATION_TEXT,
	MAX_CERTIFICATION_TEXT_LENGTH,
} from '@dealstack/shared/lib/common/business/kycCertification';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type {
	CertificationTextEditorState,
	CertificationTextSource,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';

export const useCertificationTextEditor = ({
	savedText,
	save,
	isSaving,
}: CertificationTextSource): CertificationTextEditorState => {
	const tMipManage = useTranslationMipManage();
	// null until the manager edits, so the field tracks the saved value without a sync effect.
	const [draft, setDraft] = useState<string | null>(null);

	const persisted = savedText ?? DEFAULT_CERTIFICATION_TEXT;
	const value = draft ?? persisted;
	const trimmed = value.trim();

	const handleSave = async () => {
		try {
			// Storing null keeps the programme on the platform default if that wording changes.
			await save(trimmed === DEFAULT_CERTIFICATION_TEXT ? null : trimmed);
		} catch {
			// The draft is kept, so a failed save can be retried without retyping it.
			showToastV2({
				state: 'error',
				title: tMipManage('kyc_settings.certification.text.updated_error'),
			});
			return;
		}

		setDraft(null);
		showToastV2({ title: tMipManage('kyc_settings.certification.text.updated_success') });
	};

	return {
		value,
		setValue: setDraft,
		resetToDefault: () => setDraft(DEFAULT_CERTIFICATION_TEXT),
		save: () => void handleSave(),
		isDirty: trimmed !== persisted,
		isSaving,
		isValid: trimmed.length > 0 && trimmed.length <= MAX_CERTIFICATION_TEXT_LENGTH,
	};
};
